import { Defaults } from "@/models/Defaults";
import { Quote } from "@/models/Quote";
import {
  convertBooleanToYesNo,
  convertYesNoToBoolean,
  cardTypeLong,
} from "@/utils/Utils";
import { BillingAddressType } from "@/models/PaymentMethod";
import { PaymentOptions } from "@/models/QuoteInsurance";
import axios from "axios";

import { LocalDate, DateTimeFormatter } from "@js-joda/core";

export class AssurantData {
  /**
   * Get assurant quote
   * @param quote
   * @returns
   */
  static async getQuote(
    quote: Quote,
    isRequote: boolean
  ): Promise<{
    error: boolean;
    message: string;
    result: any;
    denialNotice: string;
  }> {
    const result = {
      error: false,
      message: "",
      result: [],
      denialNotice: "",
    };

    const tokenResponse = await this.validateToken(quote);
    let coverageParm: any[] = [];
    if (isRequote) {
      coverageParm = [
        {
          name: "liabilityAmount",
          value:
            quote.rentersInsurance.quoteCoverageDetails.liabilityAmount.toString(),
        },
        {
          name: "personalPropertyAmount",
          value:
            quote.rentersInsurance.quoteCoverageDetails.personalPropertyAmount.toString(),
        },
        {
          name: "deductible",
          value:
            quote.rentersInsurance.quoteCoverageDetails.deductible.toString(),
        },
        {
          name: "hasEarthquake",
          value: convertBooleanToYesNo(
            quote.rentersInsurance.quoteCoverageDetails.earthquake
          ),
        },
        {
          name: "hasIdentityFraud",
          value: convertBooleanToYesNo(
            quote.rentersInsurance.quoteCoverageDetails.identityFraud
          ),
        },
        {
          name: "hasReplacementCost",
          value: convertBooleanToYesNo(
            quote.rentersInsurance.quoteCoverageDetails.replacementCost
          ),
        },
        {
          name: "iuiType",
          value: quote.rentersInsurance.quoteCoverageDetails.iuiTypes, //not used ,has to be passed
        },
        {
          name: "hasPetDamage",
          value: convertBooleanToYesNo(
            quote.rentersInsurance.quoteCoverageDetails.petDamage
          ), //not used, has to be passed
        },
        {
          name: "hasSewerBackup",
          value: convertBooleanToYesNo(
            quote.rentersInsurance.quoteCoverageDetails.waterBackup
          ),
        },
      ];
    }

    if (tokenResponse.error) {
      result.error = true;
      result.message = tokenResponse.message;
    } else {
      const data = {
        transaction: {
          clientId: Defaults.getInstance().assurantClientId,
          productType: Defaults.getInstance().assurantProductType,
          policyEffectiveDate: quote.info.coverageStartDate,
          jobNumber: Defaults.getInstance().assurantJobNumber,
          workItemId: isRequote ? quote.rentersInsurance.workitemId : "",
        },
        policyHolder: {
          insured: {
            address: {
              address1: quote.address.address,
              address2: quote.address.apt,
              city: quote.address.city,
              state: quote.address.state.short,
              postalCode: quote.address.zip,
            },
          },
        },
        underWriting: {
          questions: [
            {
              questionName: "dwellingType",
              answer: quote.info.propertyType,
            },
            {
              questionName: "isManufacturedHome",
              answer: convertBooleanToYesNo(quote.info.isManufactured),
            },
            {
              questionName: "isMasonryConstruction",
              answer: convertBooleanToYesNo(quote.info.isMasonryConstruction),
            },
            {
              questionName: "mustHaveInsurance",
              answer: convertBooleanToYesNo(quote.info.requiredRenters),
            },
            {
              questionName: "numberOfLosses",
              answer: quote.info.numberOfLosses,
            },
            {
              questionName: "lastLossDate",
              answer: quote.info.lastLossDate,
            },
            {
              questionName: "hadAnimalInjury",
              answer: convertBooleanToYesNo(quote.info.animalHarm),
            },
            {
              questionName: "leaseEffectiveDate",
              answer: quote.info.coverageStartDate,
            },
          ],
        },
        coverage: {
          parameters: coverageParm,
        },
      };
      let response;
      try {
        response = await axios.post(
          process.env.VUE_APP_ASSURANT_API_URL + "quote",
          data,
          {
            headers: { AccessToken: `${quote.assurantToken}` },
          }
        );
      } catch (error) {
        result.error = true;
        result.message = "Unknown error";
      }

      if (!result.error) {
        if (response?.data.errorDetails) {
          const respError = response.data.errorDetails;
          result.error = true;
          result.message = respError.error.errorMessage
            ? `${respError.error.errorMessage}`
            : "Unknown error";
          result.denialNotice = respError.error.denialNotice;
          if (respError && respError.error && respError.error.errors) {
            respError.error.errors.forEach((element: any) => {
              result.message += ` "${element.fieldName}- ${element.description}"`;
            });
          }
        }

        if (
          response?.data.transactionDetails !== undefined &&
          response?.data.transactionDetails.responseMessage !== ""
        ) {
          result.error = true;
          result.message = response?.data.transactionDetails.responseMessage;
        }
      }

      result.result = response?.data;
    }

    return new Promise<{
      error: boolean;
      message: string;
      result: any;
      denialNotice: string;
    }>((resolve) => {
      resolve(result);
    });
  }
  static async readAssurantData(
    quote: Quote,
    isRequote: boolean
  ): Promise<{
    error: boolean;
    message: string;
    denialNotice: string;
  }> {
    const result = {
      error: false,
      message: "",
      denialNotice: "",
    };
    const quoteResult = await AssurantData.getQuote(quote, isRequote);
    if (quoteResult.error) {
      result.error = true;
      result.message = quoteResult.message;
      quoteResult.result.transactionDetails?.denialNotice
        ? (result.denialNotice =
            quoteResult.result.transactionDetails.denialNotice)
        : "";
    } else {
      AssurantData.processAssurantQuotes(quoteResult, quote);
      result.error = false;
    }

    return new Promise<{
      error: boolean;
      message: string;
      denialNotice: string;
    }>((resolve) => {
      resolve(result);
    });
  }

  static async processAssurantQuotes(quoteResult: any, quote: Quote) {
    quote.rentersInsurance.rentersApiOk = true;

    const rentersQuote = quoteResult.result;
    const coverageCue = rentersQuote.coveragesCue.parameters;
    const coverageDetails = rentersQuote.coverageDetails.parameters;

    //CoverageQue options
    quote.info.quoteCoverageCue.hasIdFraud = coverageCue.find(
      (cueParm: any) => cueParm.name == "hasIdFraud"
    ).value;
    quote.info.quoteCoverageCue.hasInternetCredit = coverageCue.find(
      (cueParm: any) => cueParm.name == "hasInternetCredit"
    ).value;
    quote.info.quoteCoverageCue.hasLossFreeCredit = coverageCue.find(
      (cueParm: any) => cueParm.name == "hasLossFreeCredit"
    ).value;
    quote.info.quoteCoverageCue.hasConstructionType = coverageCue.find(
      (cueParm: any) => cueParm.name == "hasConstructionType"
    ).value;
    quote.info.quoteCoverageCue.hasHurricaneDeductible = coverageCue.find(
      (cueParm: any) => cueParm.name == "hasHurricaneDeductible"
    ).value;
    quote.info.quoteCoverageCue.hasPetDamage = coverageCue.find(
      (cueParm: any) => cueParm.name == "hasPetDamage"
    ).value;
    quote.info.quoteCoverageCue.hasWaterBackup = coverageCue.find(
      (cueParm: any) => cueParm.name == "hasWaterBackup"
    ).value;
    quote.info.quoteCoverageCue.hasWaterDamageLiability = coverageCue.find(
      (cueParm: any) => cueParm.name == "hasWaterDamageLiability"
    ).value;
    quote.info.quoteCoverageCue.hasReplacementCost = coverageCue.find(
      (cueParm: any) => cueParm.name == "hasReplacementCost"
    ).value;
    quote.info.quoteCoverageCue.hasEarthquake = coverageCue.find(
      (cueParm: any) => cueParm.name == "hasEarthquake"
    ).value;
    quote.info.quoteCoverageCue.personalLiabilityOptions = coverageCue.find(
      (cueParm: any) => cueParm.name == "personalLiabilityOptions"
    ).value;
    quote.info.quoteCoverageCue.deductibleOptions = coverageCue.find(
      (cueParm: any) => cueParm.name == "deductibleOptions"
    ).value;
    quote.info.quoteCoverageCue.personalPropertyMin = coverageCue.find(
      (cueParm: any) => cueParm.name == "personalPropertyMin"
    ).value;
    quote.info.quoteCoverageCue.personalPropertyMax = coverageCue.find(
      (cueParm: any) => cueParm.name == "personalPropertyMax"
    ).value;
    quote.info.quoteCoverageCue.personalPropertyIncrement = coverageCue.find(
      (cueParm: any) => cueParm.name == "personalPropertyIncrement"
    ).value;
    //CoverageDetails
    quote.rentersInsurance.quoteCoverageDetails.liabilityAmount =
      coverageDetails.find(
        (coverageParm: any) => coverageParm.name == "liabilityAmount"
      )?.value;
    quote.rentersInsurance.quoteCoverageDetails.deductible =
      coverageDetails.find(
        (coverageParm: any) => coverageParm.name == "deductible"
      )?.value;
    quote.rentersInsurance.quoteCoverageDetails.petDamage =
      convertYesNoToBoolean(
        coverageDetails.find(
          (coverageParm: any) => coverageParm.name == "petDamage"
        )?.value
      );
    quote.rentersInsurance.quoteCoverageDetails.iuiTypes = coverageDetails.find(
      (coverageParm: any) => coverageParm.name == "iuiTypes"
    )?.value;
    quote.rentersInsurance.quoteCoverageDetails.hurricaneDeductible =
      coverageDetails.find(
        (coverageParm: any) => coverageParm.name == "hurricaneDeductible"
      )?.value;
    quote.rentersInsurance.quoteCoverageDetails.identityFraud =
      convertYesNoToBoolean(
        coverageDetails.find(
          (coverageParm: any) => coverageParm.name == "identityFraud"
        )?.value
      );
    quote.rentersInsurance.quoteCoverageDetails.dwelingCreditSurcharge =
      coverageDetails.find(
        (coverageParm: any) => coverageParm.name == "dwelingCreditSurcharge"
      )?.value;
    quote.rentersInsurance.quoteCoverageDetails.internetCredit =
      coverageDetails.find(
        (coverageParm: any) => coverageParm.name == "internetCredit"
      )?.value;
    quote.rentersInsurance.quoteCoverageDetails.lossFreeCredit =
      coverageDetails.find(
        (coverageParm: any) => coverageParm.name == "lossFreeCredit"
      )?.value;
    quote.rentersInsurance.quoteCoverageDetails.mustHaveInsurance =
      coverageDetails.find(
        (coverageParm: any) => coverageParm.name == "mustHaveInsurance"
      )?.value;
    quote.rentersInsurance.quoteCoverageDetails.personalPropertyAmount =
      coverageDetails.find(
        (coverageParm: any) => coverageParm.name == "personalPropertyAmount"
      )?.value;
    quote.rentersInsurance.quoteCoverageDetails.isMasonryConstruction =
      coverageDetails.find(
        (coverageParm: any) => coverageParm.name == "isMasonryConstruction"
      )?.value;
    quote.rentersInsurance.quoteCoverageDetails.replacementCost =
      convertYesNoToBoolean(
        coverageDetails.find(
          (coverageParm: any) => coverageParm.name == "replacementCost"
        )?.value
      );
    quote.rentersInsurance.quoteCoverageDetails.waterBackup =
      convertYesNoToBoolean(
        coverageDetails.find(
          (coverageParm: any) => coverageParm.name == "waterBackup"
        )?.value
      );
    quote.rentersInsurance.quoteCoverageDetails.earthquake =
      convertYesNoToBoolean(
        coverageDetails.find(
          (coverageParm: any) => coverageParm.name == "earthquake"
        )?.value
      );

    quote.rentersInsurance.workitemId =
      rentersQuote.transactionDetails.workItemId;
    const annualPayment =
      rentersQuote.transactionDetails.payments.schedules.find(
        (scheduleP: any) => scheduleP.numberOfPayments == 1
      );
    const fourPayment = rentersQuote.transactionDetails.payments.schedules.find(
      (scheduleP: any) => scheduleP.numberOfPayments == 4
    );
    const monthlyPayment =
      rentersQuote.transactionDetails.payments.schedules.find(
        (scheduleP: any) => scheduleP.numberOfPayments == 11
      );

    if (annualPayment !== undefined) {
      if (quote.rentersInsurance.paymentOption == PaymentOptions.Annual) {
        quote.rentersInsurance.annualAmount = parseFloat(
          annualPayment.downPaymentAmount
        );
        quote.rentersInsurance.serviceFee =
          annualPayment.serviceFee == null
            ? 0.0
            : parseFloat(annualPayment.serviceFee);
        quote.rentersInsurance.installmentAmount = parseFloat(
          annualPayment.installmentAmount
        );
        quote.rentersInsurance.dueToday = quote.rentersInsurance.annualAmount;
        quote.rentersInsurance.numberOfPayments = 1;
      }
    }
    if (fourPayment !== undefined) {
      if (quote.rentersInsurance.paymentOption == PaymentOptions.FourPay) {
        quote.rentersInsurance.fourPayAmount = parseFloat(
          fourPayment.downPaymentAmount
        );
        quote.rentersInsurance.installmentAmount = parseFloat(
          fourPayment.installmentAmount
        );
        quote.rentersInsurance.serviceFee = parseFloat(fourPayment.serviceFee);
        quote.rentersInsurance.dueToday = quote.rentersInsurance.fourPayAmount;
        quote.rentersInsurance.numberOfPayments = 4;
      }
    }
    if (monthlyPayment !== undefined) {
      if (quote.rentersInsurance.paymentOption == PaymentOptions.Monthly) {
        quote.rentersInsurance.monthlyAmount = parseFloat(
          monthlyPayment.downPaymentAmount
        );
        quote.rentersInsurance.installmentAmount = parseFloat(
          monthlyPayment.installmentAmount
        );
        quote.rentersInsurance.serviceFee = parseFloat(
          monthlyPayment.serviceFee
        );
        quote.rentersInsurance.dueToday = quote.rentersInsurance.monthlyAmount;
        quote.rentersInsurance.numberOfPayments = 11;
      }
    }
    quote.rentersInsurance.retailPremium = parseFloat(
      rentersQuote.premiumDetails.premiumAmount
    );
  }
  /**
   * Issues assurant policy
   * @param quote
   * @returns
   */
  static async issuePolicy(quote: Quote): Promise<{
    error: boolean;
    message: string;
    result: any;
  }> {
    const result = {
      error: false,
      message: "",
      result: {},
    };

    const tokenResponse = await this.validateToken(quote);

    if (tokenResponse.error) {
      result.error = true;
      result.message = tokenResponse.message;
    } else {
      const mailingAddress = this.getMailingAddress(quote);
      const billingAddress = this.getBillingAddress(quote);
      const phoneNumAll = quote.checkout.info.phone;
      const phoneNum = phoneNumAll.replace(/[^a-zA-Z0-9]/g, "");
      const dueTodayAmount = Number(quote.rentersInsurance.dueToday);
      const paymentAmount = Number.isFinite(dueTodayAmount)
        ? dueTodayAmount.toFixed(2)
        : "0.00";
      const data = {
        transaction: {
          workItemId: quote.rentersInsurance.workitemId,
          productType: Defaults.getInstance().assurantProductType,
          clientId: Defaults.getInstance().assurantClientId,
          jobNumber: Defaults.getInstance().assurantJobNumber,
        },
        renters: {
          hasStateDisclosure: convertBooleanToYesNo(
            quote.info.hasStateDisclosure
          ),
          hasInterestedParty:
            quote.checkout.interestedParty.firstName !== "" ? "Y" : "N",
        },
        policyHolder: {
          payeeInfo: {
            firstName: quote.checkout.interestedParty.firstName,
            lastName: quote.checkout.interestedParty.lastName,
            address: {
              address1: quote.checkout.interestedParty.address.address,
              address2: quote.checkout.interestedParty.address.apt,
              city: quote.checkout.interestedParty.address.city,
              state: quote.checkout.interestedParty.address.state.short,
              postalCode: quote.checkout.interestedParty.address.zip,
            },
          },
          insured: {
            firstName: quote.checkout.info.firstName,
            middleInitial: "",
            lastName: quote.checkout.info.lastName,
            phoneNumber: phoneNum, //has to be 10 char or returns error as empty
            canText: "N",
            emailAddress: quote.checkout.info.email,
            isPaperless: convertBooleanToYesNo(
              quote.checkout.receiveByEmailConsent
            ),
            agreedToEBC: "Y",
            ssn: "",
            communityName: "",
            insuredDateOfBirth: quote.checkout.info.dob,
            address: {
              address1: quote.address.address,
              address2: quote.address.apt,
              city: quote.address.city,
              state: quote.address.state.short,
              postalCode: quote.address.zip,
            },
            additionalInsured:
              quote.checkout.roommates.length === 0
                ? null
                : quote.checkout.roommates,
          },
          mailingAddress: mailingAddress,
          billingAddress: billingAddress,
        },
        billing: {
          paymentType: "cc",
          numberOfPayments: quote.rentersInsurance.numberOfPayments.toString(),
          paymentAmount: paymentAmount,
          billDate: LocalDate.now().format(
            DateTimeFormatter.ofPattern("yyyy-MM-d")
          ),
          ccInfo: {
            cardType: cardTypeLong(quote.checkout.paymentMethod.cardNumber),
            cardNumber: quote.checkout.paymentMethod.cardNumber,
            cardCvc: quote.checkout.paymentMethod.cvv,
            cardExpMonth: quote.checkout.paymentMethod.expDateMonth,
            cardExpYear: quote.checkout.paymentMethod.expDateYear,
          },
          bankInfo: {
            accountNumber: "",
            routingNumber: "",
          },
        },
        includeAppFile: "N",
      };
      let response;
      try {
        response = await axios.post(
          process.env.VUE_APP_ASSURANT_API_URL + "policy",
          data,
          {
            headers: { AccessToken: `${quote.assurantToken}` },
          }
        );
      } catch (error) {
        result.error = true;
        result.message = "Unknown error";
      }

      if (!result.error) {
        if (response?.data.errorDetails) {
          const respError = response.data.errorDetails;

          result.error = true;
          result.message = respError.error.errorMessage
            ? `${respError.error.errorMessage}`
            : "Unknown error";
          if (respError && respError.error && respError.error.errors) {
            respError.error.errors.forEach((element: any) => {
              result.message += ` "${element.fieldName}- ${element.description}"`;
            });
          }
        }

        if (
          response?.data.transactionDetails !== undefined &&
          response?.data.transactionDetails?.responseMessage !== ""
        ) {
          result.error = true;
          result.message = response?.data.transactionDetails.responseMessage;
        }
      }
      result.result = response?.data;
    }

    return new Promise<{
      error: boolean;
      message: string;
      result: any;
    }>((resolve) => {
      resolve(result);
    });
  }
  static getMailingAddress(quote: Quote) {
    const mailingAddress = quote.checkout.useSameAddressForMailing
      ? {
          address1: quote.address.address,
          address2: quote.address.apt,
          city: quote.address.city,
          state: quote.address.state.short,
          postalCode: quote.address.zip,
        }
      : {
          address1: quote.checkout.mailingAddress.address,
          address2: quote.checkout.mailingAddress.apt,
          city: quote.checkout.mailingAddress.city,
          state: quote.checkout.mailingAddress.state.short,
          postalCode: quote.checkout.mailingAddress.zip,
        };

    return mailingAddress;
  }

  static getBillingAddress(quote: Quote) {
    if (
      quote.checkout.paymentMethod.billingAddressType ==
      BillingAddressType.Property
    ) {
      return {
        firstName: quote.checkout.info.firstName,
        middleInitial: "",
        lastName: quote.checkout.info.lastName,
        address1: quote.address.address,
        address2: quote.address.apt,
        city: quote.address.city,
        state: quote.address.state.short,
        postalCode: quote.address.zip,
      };
    } else if (
      quote.checkout.paymentMethod.billingAddressType ==
      BillingAddressType.Mailing
    ) {
      if (quote.checkout.useSameAddressForMailing) {
        return {
          firstName: quote.checkout.info.firstName,
          middleInitial: "",
          lastName: quote.checkout.info.lastName,
          address1: quote.address.address,
          address2: quote.address.apt,
          city: quote.address.city,
          state: quote.address.state.short,
          postalCode: quote.address.zip,
        };
      } else {
        return {
          firstName: quote.checkout.info.firstName,
          middleInitial: "",
          lastName: quote.checkout.info.lastName,
          address1: quote.checkout.mailingAddress.address,
          address2: quote.checkout.mailingAddress.apt,
          city: quote.checkout.mailingAddress.city,
          state: quote.checkout.mailingAddress.state.short,
          postalCode: quote.checkout.mailingAddress.zip,
        };
      }
    } else if (
      quote.checkout.paymentMethod.billingAddressType ==
      BillingAddressType.Different
    ) {
      return {
        firstName: quote.checkout.paymentMethod.billingAddress.firstName,
        middleInitial: "",
        lastName: quote.checkout.paymentMethod.billingAddress.lastName,
        address1: quote.checkout.paymentMethod.billingAddress.address,
        address2: quote.checkout.paymentMethod.billingAddress.apt,
        city: quote.checkout.paymentMethod.billingAddress.city,
        state: quote.checkout.paymentMethod.billingAddress.state.short,
        postalCode: quote.checkout.paymentMethod.billingAddress.zip,
      };
    } else {
      return {
        firstName: quote.checkout.info.firstName,
        lastName: quote.checkout.info.lastName,
        address1: quote.address.address,
        address2: quote.address.apt,
        city: quote.address.city,
        state: quote.address.state.short,
        postalCode: quote.address.zip,
      };
    }
  }

  /**
   * Gets token from assurant API
   */
  static async getToken(): Promise<{
    error: boolean;
    message: string;
    result: any;
  }> {
    const params = new URLSearchParams();
    const result = {
      error: false,
      message: "",
      result: [],
    };
    let response;
    params.append("grant_type", "client_credentials");
    params.append("scope", "groups");

    try {
      response = await axios.post(
        process.env.VUE_APP_ASSURANT_API_URL + "token",
        params
      );
    } catch (error) {
      result.error = true;
      result.message = "Unknown error";
    }

    if (response?.data.error) {
      result.error = true;
      result.message = response?.data.error_description;
    }

    result.result = response?.data;

    return new Promise<{
      error: boolean;
      message: string;
      result: any;
    }>((resolve) => {
      resolve(result);
    });
  }

  /**
   * Checks if there's a token for the assurant API, and if there is, it checks if
   * it is expired
   * @param quote
   * @returns validToken
   */
  static checkToken(quote: Quote): boolean {
    let validToken = false;

    if (!quote.assurantToken) {
      validToken = false;
    } else if (quote.assurantExpirationTime) {
      const expDate = LocalDate.parse(quote.assurantExpirationTime);
      validToken = expDate < LocalDate.now();
    }

    return validToken;
  }
  /**
   * Checks if there's a valid assurant api token. If there's not it tries to acquire one.
   * @param quote
   * @returns
   */
  static async validateToken(quote: Quote): Promise<{
    error: boolean;
    message: string;
  }> {
    const result = {
      error: false,
      message: "",
    };

    if (!this.checkToken(quote)) {
      const tokenResponse = await this.getToken();

      if (!tokenResponse.error) {
        quote.assurantToken = tokenResponse.result.access_token;
        quote.assurantExpirationTime = tokenResponse.result.expirationTime;
      } else {
        result.error = true;
        result.message = tokenResponse.message;
      }
    }

    return new Promise<{
      error: boolean;
      message: string;
    }>((resolve) => {
      resolve(result);
    });
  }
}
